import type { PreviewApplyVetterPricesQuery } from '@codezo/graphql';
import { Fragment } from 'react';
import { cn } from '../../../lib/utils';
import { CurrencyText } from '../../CurrencyText';
import { applyPriceEdit, markupFromSale, type PriceTriple } from '../../pricing/tier-pricing';
import { Checkbox } from '../../ui/checkbox';
import { AffixInput } from './AffixInput';
import { ApplyShippingLines, type ShipDraft } from './ApplyShippingLines';

export type ProductPreview =
	PreviewApplyVetterPricesQuery['previewApplyVetterPrices']['products'][number];
export type QuantityPreview = ProductPreview['quantities'][number];

export interface QuotedQuantity {
	quantityId: string;
	quantity: number;
}

/** No client price typed: the quantity keeps its margin rule. */
export const KEEP_RULE: PriceTriple = { cost: '', salePrice: '', markup: '', entryMode: 'sale' };

const HEAD = 'px-2 pb-2 text-xs font-normal text-text-4';
const CELL = 'px-2 py-3 align-middle';
/** Per-piece prices: 2 decimals, up to 4 only when the figure needs them. */
const num = (n: number | null | undefined) =>
	n == null
		? '–'
		: n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * One supplier's prices for one product, a row per quantity they priced: their
 * unit cost, the client price, the markup, and the margin and client total that
 * follow. The client price is set the way the offer page prices a tier, as a
 * sale price or a markup, each filling the other. Only the one typed is sent,
 * and an empty pair keeps the quantity's rule. The figures are the backend's dry
 * run, the same calculation Apply saves, in the display currency.
 */
export function ApplyPricesProductTable({
	quantities,
	picked,
	onToggle,
	prices,
	onPriceChange,
	currency,
	rowOf,
	stale,
	underCost,
	shipping,
	onShippingChange,
}: {
	/** Every quantity the supplier priced for this product. */
	quantities: QuotedQuantity[];
	picked: string[];
	onToggle: (quantityId: string, on: boolean) => void;
	/** The client price typed per quantity; missing means keep the rule. */
	prices: Record<string, PriceTriple>;
	onPriceChange: (quantityId: string, next: PriceTriple) => void;
	/** The display currency: previews come back in it and typed prices are in it. */
	currency: string;
	/** The last dry run's row for a quantity. */
	rowOf: (quantityId: string) => QuantityPreview | undefined;
	/** The dry run is catching up with the latest change; the figures shown are the last ones. */
	stale: boolean;
	/** Quantities whose typed sale price is under the supplier's cost. */
	underCost: string[];
	/** Typed delivery prices, per quantity, per line id. */
	shipping: Record<string, Record<string, ShipDraft>>;
	onShippingChange: (quantityId: string, lineId: string, next: ShipDraft) => void;
}) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full min-w-[600px] text-sm">
				<thead>
					<tr className="border-b border-line-1">
						<th className={cn(HEAD, 'text-left')} colSpan={2}>
							Quantity
						</th>
						<th className={cn(HEAD, 'text-right')}>Unit cost</th>
						<th className={cn(HEAD, 'text-right')}>Client price / unit</th>
						<th className={cn(HEAD, 'text-right')}>Markup</th>
						<th className={cn(HEAD, 'text-right')}>Margin</th>
						<th className={cn(HEAD, 'text-right')}>Client total</th>
					</tr>
				</thead>
				<tbody>
					{quantities.map((q) => {
						const on = picked.includes(q.quantityId);
						const row = on ? rowOf(q.quantityId) : undefined;
						const price = prices[q.quantityId] ?? KEEP_RULE;
						// Typed against the supplier's cost, which the price doesn't move.
						const cost = row ? String(row.after.cost) : '';
						const edit = (field: 'salePrice' | 'markup', value: string) =>
							onPriceChange(
								q.quantityId,
								applyPriceEdit({ ...price, cost }, field, value)
							);
						const priced = !!row && !row.unpricedForClient;
						const figure = cn(
							CELL,
							'text-right font-mono whitespace-nowrap',
							stale && 'opacity-50'
						);

						// Every delivery line of the quantity, under its row, each one editable.
						const lines = on ? (row?.shipping ?? []) : [];
						return (
							<Fragment key={q.quantityId}>
								<tr
									className={cn(
										lines.length === 0 &&
											'border-b border-line-1 last:border-b-0',
										!on && 'text-text-4'
									)}
								>
									<td className={cn(CELL, 'w-8 pr-0')}>
										<Checkbox
											checked={on}
											onCheckedChange={(v) => onToggle(q.quantityId, v)}
											aria-label={`Use the price for ${q.quantity} pcs`}
										/>
									</td>
									<td className={cn(CELL, 'text-left whitespace-nowrap')}>
										<div className={cn('font-mono', on && 'text-text-1')}>
											{q.quantity.toLocaleString('en-US')} pcs
										</div>
										<div className="text-xs text-text-4">
											{!on ? 'Not used' : row ? nowLine(row) : '…'}
										</div>
									</td>
									<td className={figure}>
										{row ? <CurrencyText amount={row.after.cost} /> : '–'}
									</td>
									<td className={cn(CELL, 'text-right')}>
										<AffixInput
											value={price.salePrice}
											onChange={(v) => edit('salePrice', v)}
											placeholder={priced ? num(row.after.unitPrice) : '–'}
											suffix={currency}
											disabled={!on}
											tone={
												underCost.includes(q.quantityId)
													? 'danger'
													: undefined
											}
											label={`Client price per unit for ${q.quantity} pcs`}
										/>
									</td>
									<td className={cn(CELL, 'text-right')}>
										<AffixInput
											value={price.markup}
											onChange={(v) => edit('markup', v)}
											placeholder={
												priced
													? markupFromSale(
															cost,
															String(row.after.unitPrice)
														) || '–'
													: '–'
											}
											suffix="%"
											disabled={!on}
											width="w-[76px]"
											label={`Markup for ${q.quantity} pcs`}
										/>
									</td>
									<td className={figure}>
										{priced && row.after.marginPercentage != null
											? `${row.after.marginPercentage.toFixed(0)}%`
											: '–'}
									</td>
									<td className={cn(figure, on && 'text-text-1')}>
										{priced ? (
											<CurrencyText amount={row.after.totalPrice} />
										) : (
											'–'
										)}
									</td>
								</tr>
								{lines.length > 0 && (
									<tr className="border-b border-line-1 last:border-b-0">
										<td />
										<td colSpan={6} className="px-2 pb-3">
											<ApplyShippingLines
												lines={lines}
												drafts={shipping[q.quantityId] ?? {}}
												onChange={(lineId, next) =>
													onShippingChange(q.quantityId, lineId, next)
												}
												currency={currency}
												stale={stale}
											/>
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

/** Under the quantity: what the offer has for it now, or that it's new. */
function nowLine(row: QuantityPreview): string {
	const before = row.before;
	if (row.createsPrice || !before) return 'New on offer';
	if (before.unitPrice == null) return `Now ${num(before.cost)} · no client price`;
	const markup = markupFromSale(String(before.cost), String(before.unitPrice));
	return `Now ${num(before.cost)} → ${num(before.unitPrice)}${markup ? ` · ${markup}%` : ''}`;
}
